import React, { useMemo } from 'react';
import { Navigation, AlertTriangle } from 'lucide-react';
import { Coordinates, RouteFollowPoi } from '../../types';
import { getBearing } from '../../services/geometryUtils';

interface NavigationHUDProps {
  userPosition: Coordinates | null;
  targetLocation: Coordinates | null;
  targetName: string;
  distanceToTargetM: number | null;
  speedKmh: number | null;
  isOffRoute: boolean;
  distanceFromRouteM: number | null;
  nearestRoutePoint: Coordinates | null;
  userHeading: number | null;
  progressPercent: number;
  nextPoi: RouteFollowPoi | null;
  nextPoiDistanceM: number | null;
}

const formatDist = (m: number | null): string => {
  if (m == null) return '';
  if (m < 1000) return `${Math.round(m)}m`;
  return `${(m / 1000).toFixed(1)}km`;
};

const NavigationHUD: React.FC<NavigationHUDProps> = ({
  userPosition,
  targetLocation,
  targetName,
  distanceToTargetM,
  speedKmh,
  isOffRoute,
  distanceFromRouteM,
  nearestRoutePoint,
  userHeading,
  progressPercent,
  nextPoi,
  nextPoiDistanceM,
}) => {
  // Bearing from user to target (or back to route if off-route)
  const arrowRotation = useMemo(() => {
    if (!userPosition) return 0;
    const dest = isOffRoute && nearestRoutePoint ? nearestRoutePoint : targetLocation;
    if (!dest) return 0;
    const bearing = getBearing(
      userPosition.latitude, userPosition.longitude,
      dest.latitude, dest.longitude
    );
    // Compensate for user's heading so arrow is relative to direction of travel
    const heading = userHeading ?? 0;
    return bearing - heading;
  }, [userPosition, targetLocation, nearestRoutePoint, isOffRoute, userHeading]);

  const displayDist = isOffRoute
    ? formatDist(distanceFromRouteM)
    : formatDist(distanceToTargetM);

  const displayLabel = isOffRoute
    ? 'Tilbake til ruten'
    : targetName || 'Neste mål';

  return (
    <div className="absolute top-0 left-0 right-0 z-[3200] pointer-events-auto safe-area-top">
      <div className={`mx-2 mt-2 flex items-center gap-2.5 px-3 py-2 rounded-2xl shadow-lg border transition-colors duration-300 ${
        isOffRoute
          ? 'bg-red-500 border-red-400/50'
          : 'bg-white border-stone-200'
      }`}>

        {/* Direction arrow */}
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
          isOffRoute ? 'bg-white/20' : 'bg-emerald-500/15'
        }`}>
          {isOffRoute ? (
            <AlertTriangle
              size={20}
              className="text-white animate-pulse"
            />
          ) : (
            <Navigation
              size={20}
              className="text-emerald-500 transition-transform duration-500"
              style={{ transform: `rotate(${arrowRotation}deg)` }}
            />
          )}
        </div>

        {/* Target info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-[13px] font-black truncate ${
              isOffRoute ? 'text-white' : 'text-stone-800'
            }`}>
              {displayLabel}
            </span>
            {displayDist && (
              <span className={`text-[12px] font-bold shrink-0 ${
                isOffRoute ? 'text-white/80' : 'text-emerald-500'
              }`}>
                {displayDist}
              </span>
            )}
          </div>

          {/* Progress bar */}
          <div className={`w-full h-1 rounded-full mt-1 ${
            isOffRoute ? 'bg-white/20' : 'bg-stone-200'
          }`}>
            <div
              className={`h-full rounded-full transition-all duration-700 ease-out ${
                isOffRoute ? 'bg-white/60' : 'bg-emerald-500'
              }`}
              style={{ width: `${Math.min(100, progressPercent)}%` }}
            />
          </div>
        </div>

        {/* Speed */}
        {speedKmh !== null && (
          <div className={`flex flex-col items-center shrink-0 px-1.5 ${
            isOffRoute ? 'text-white' : ''
          }`}>
            <span className={`text-[16px] font-black leading-none ${
              isOffRoute ? 'text-white' : 'text-stone-800'
            }`}>
              {Math.round(speedKmh)}
            </span>
            <span className={`text-4xs font-medium uppercase tracking-wider ${
              isOffRoute ? 'text-white/70' : 'text-stone-400'
            }`}>
              km/t
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(NavigationHUD);
